import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid2 as Grid, Stack, Box} from 'views/components';

import FilterBar from './scene/filter/FilterBar';
import Header from './scene/Header';
import List from './scene/list/List';
import MapFilter from './scene/filter/MapFilter';

import {simplifyQuestions} from 'utils/user_bio';

const defaultSelection = (filterOptions, showMapFilter) => {
  return {
    order: {orderBy: 'creationTime', orderDirc: 'DESC'},
    filter: {
      status: {
        filterData: filterOptions['status'],
        optionSelected: filterOptions.status.defaultSelected,
      },
      ...(showMapFilter ? {location: {
        filterData: filterOptions['location'],
        optionSelected: 'disabled',
      }} : {}),
    },
  };
};

const CaregiverListScene = (props) => {
  const {
    caregiverApi,
    caregiverDetailBasePath,
    filterOptions,
    formatLanguageFilter,
    showMapFilter,
    statusTranslationKey,
    titleExpression,
  } = props;
  const [reload, setReload] = React.useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(new Date());
  const [selection, setSelection] = React.useState(() => defaultSelection(filterOptions, showMapFilter));
  const [data, setData] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [mapBounds, setMapBounds] = React.useState(null);
  const [highlightedCaregiverId, setHighlightedCaregiverId] = React.useState(null);
  const [caregiversForMap, setCaregiversForMap] = React.useState([]);

  // Used to cancel stale loadData results: increment to mark previous requests stale
  const requestIdRef = React.useRef(0);

  const resetFilter = () => {
    setSelection(defaultSelection(filterOptions, showMapFilter));
  };

  const updateFilter = (filter, option) => {
    setSelection((prev) => {
      const newFilter = {...prev.filter};

      if (option) {
        if (filter.multiSelect) {
          const selected = newFilter[filter.key]?.optionSelected || [];

          const doesItExist = selected.find((k) => k === option.key);
          if (!doesItExist) {
          // Add option
            newFilter[filter.key] = {
              filterData: filter,
              optionSelected: [...selected, option.key],
            };
          } else {
            // Remove option
            newFilter[filter.key] = {
              ...newFilter[filter.key],
              optionSelected: selected.filter((k) => k !== option.key),
            };
          }
        } else {
          // Single select: replace value
          newFilter[filter.key] = {
            filterData: filter,
            optionSelected: option.key,
          };
        }
      } else {
        // Remove filter entirely
        delete newFilter[filter.key];
      }

      setLastEvaluatedKey(null);
      return {...prev, filter: newFilter};
    });
  };

  // Check if map filter is enabled
  const isMapFilterEnabled = selection.filter.location?.optionSelected === 'enabled';

  const loadData = React.useCallback(async () => {
    // Capture current request id so this call can be ignored if a newer one starts
    const reqId = requestIdRef.current;
    setIsLoading(true);
    try {
      const filter = [];
      for (const filterSelected of Object.values(selection.filter)) {
        if (filterSelected.filterData.key === 'location') continue;

        if (filterSelected.filterData.key === 'certification') {
          filter.push(...filterSelected.optionSelected.map((optKey) => ({key: optKey, value: true})));
          continue;
        }

        if (filterSelected.filterData.key === 'languages') {
          const labels = filterSelected.optionSelected.map((k) => filterOptions.languages.options[k].label);
          filter.push({
            key: 'languages',
            value: formatLanguageFilter(labels),
          });
          continue;
        }

        filter.push({key: filterSelected.filterData.key, value: filterSelected.optionSelected});
      }

      // Add buffer bounding box filters when map filter is enabled and buffer bounds are set
      if (isMapFilterEnabled && mapBounds) {
        filter.push({key: 'latitude', value: `[${mapBounds.minLat} TO ${mapBounds.maxLat}]`});
        filter.push({key: 'longitude', value: `[${mapBounds.minLng} TO ${mapBounds.maxLng}]`});
      }

      const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
      const result = await caregiverApi.queryCaregiver(filter, sortBy, lastEvaluatedKey);

      // If another request started after this one, drop the results (cancelled/stale)
      if (reqId !== requestIdRef.current) return;

      const caregivers = result.list.map((caregiver) => ({
        ...caregiver,
        detailPath: `${caregiverDetailBasePath}/${encodeURIComponent(caregiver.caregiverRn)}`,
        statusTranslationKey,
      }));
      setData((e) => [...e, ...caregivers]);
      setLastEvaluatedKey(result.lastEvaluatedKey);
    } finally {
      // Only update loading state when this call is still the latest
      if (reqId === requestIdRef.current) setIsLoading(false);
    }
  }, [selection, lastEvaluatedKey, mapBounds, caregiverApi, caregiverDetailBasePath, formatLanguageFilter, statusTranslationKey]);

  React.useEffect(() => {
    // Bump request id to mark any in-flight requests stale/cancelled
    requestIdRef.current += 1;

    setData([]);
    setLastEvaluatedKey(null);
    setHighlightedCaregiverId(null);
    loadData();
  }, [selection, reload, mapBounds]); // Reset when bounds change

  React.useEffect(() => {
    const list = data.map((caregiver) => {
      const bio = simplifyQuestions(caregiver.bio?.questions ?? {});
      const firstName = (bio?.firstName || '').trim();
      const lastName = (bio?.lastName || '').trim();
      const profileImg = bio?.profilePhoto?.[0]?.src;

      const name = [firstName, lastName].filter(Boolean).join(' ') ||
                   caregiver.caregiverName || 'Caregiver';

      return {
        id: caregiver.caregiverRn,
        name,
        profileImg,
        location: {
          lat: caregiver.latitude || null,
          lng: caregiver.longitude || null,
        },
      };
    }).filter((c) => c.location.lat && c.location.lng);

    setCaregiversForMap(list);
  }, [data]);


  const onBoundChange = (e) => {
    setMapBounds(e);
    setLastEvaluatedKey(null);
  };

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Header
          titleExpression={titleExpression}
          lastUpdatedAt={lastUpdatedAt}
          numRefresh={reload}
          setLastUpdatedAt={setLastUpdatedAt}
          setNumRefresh={setReload}
        />
      </Grid>
      <Grid size={12}>
        <Stack spacing={4} width={1} pb={{xs: 0, md: 2}}>
          <FilterBar
            selection={selection}
            filterOptions={filterOptions}
            showMapFilter={showMapFilter}
            reload={reload}
            updateFilter={updateFilter}
            onClearFilter={resetFilter}
          />
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: isMapFilterEnabled ? 6 : 12,
              }}
            >
              {
                isMapFilterEnabled &&
                <Box sx={{height: {xs: 'calc(100vh - 220px)', md: 'calc(100vh - 160px)'}, overflowY: 'auto', pr: 1}}>
                  <List
                    data={data}
                    isLoading={isLoading}
                    loadMore={loadData}
                    hasMore={!!lastEvaluatedKey}
                    highlightedCaregiverId={highlightedCaregiverId}
                    cardsPerRow={1}
                  />
                </Box>
              }
              {
                !isMapFilterEnabled &&
                <List
                  data={data}
                  isLoading={isLoading}
                  loadMore={loadData}
                  hasMore={!!lastEvaluatedKey}
                  highlightedCaregiverId={highlightedCaregiverId}
                  cardsPerRow={3}
                />
              }
            </Grid>
            {
              isMapFilterEnabled &&
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <MapFilter
                  caregivers={caregiversForMap}
                  onBoundsChange={onBoundChange}
                  onMarkerClick={setHighlightedCaregiverId}
                />
              </Grid>
            }
          </Grid>
        </Stack>
      </Grid>
    </Grid>
  );
};
CaregiverListScene.propTypes = {
  caregiverApi: PropTypes.object.isRequired,
  caregiverDetailBasePath: PropTypes.string.isRequired,
  filterOptions: PropTypes.object.isRequired,
  formatLanguageFilter: PropTypes.func.isRequired,
  showMapFilter: PropTypes.bool.isRequired,
  statusTranslationKey: PropTypes.string.isRequired,
  titleExpression: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaregiverListScene));
